import { isFullyIslandedIntent } from '../intent/gridScope';
/**
 * Stage 2 decision catalogue and status.
 *
 * The decisions mature validation needs, in the order a developer takes them:
 * the scenario-shaping choices first (power supply, cooling, phases), then the
 * design basis, delivery facts and commercial record. Each decision maps to the
 * EXISTING intent fields (and, for the power strategy, the existing
 * `typedPreferencesDefaults['power-availability'].btmStrategy`); nothing new is
 * persisted on `ProjectIntent`.
 *
 * Status is answer-based, never value-guessing: a field is decided when it has
 * a concrete value, recorded-unknown when the user said so (`user-unknown`
 * provenance or the `unknown` sentinel), and open otherwise.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { ProjectIntent } from '../../types/siteRag';
import { hasDeclaredWaterSource } from '../intent/waterSource';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import type {
  BtmStrategy,
  Stage2DecisionId,
  Stage2DecisionReview,
  Stage2DecisionStatus,
} from './stage2Types';

export interface Stage2DecisionDefinition {
  id: Stage2DecisionId;
  label: string;
  question: string;
  /** Stage 2 analyses that read the decision. */
  neededBy: readonly ReportTypeId[];
  scenarioShaping: boolean;
}

export const STAGE2_DECISIONS: readonly Stage2DecisionDefinition[] = [
  {
    id: 'power-supply',
    label: 'Power supply approach',
    question: 'Will the campus take grid power, generate on site, or both, and with which technology in each build phase?',
    neededBy: ['power-availability', 'financial', 'supply-chain-risk', 'test-fit'],
    scenarioShaping: true,
  },
  {
    id: 'cooling',
    label: 'Cooling and heat rejection',
    question: 'How will heat leave the servers, and how will the campus reject it?',
    neededBy: ['power-availability', 'test-fit', 'financial'],
    scenarioShaping: true,
  },
  {
    id: 'build-phases',
    label: 'Build phases and dates',
    question: 'Which cumulative load comes online in each phase, and on what target date?',
    neededBy: ['power-availability', 'financial', 'supply-chain-risk'],
    scenarioShaping: true,
  },
  {
    id: 'design-basis',
    label: 'Design basis',
    question: 'Which PUE, power factor, tier and redundancy should the mature analyses use?',
    neededBy: ['power-availability', 'financial', 'test-fit'],
    scenarioShaping: false,
  },
  {
    id: 'firm-capacity',
    label: 'Firm-capacity target',
    question: 'How much of the load must be firm, accredited capacity?',
    neededBy: ['power-availability', 'financial'],
    scenarioShaping: false,
  },
  {
    id: 'water-supply',
    label: 'Water supply',
    question: 'Which water source and rights will serve the campus?',
    neededBy: ['power-availability', 'test-fit'],
    scenarioShaping: false,
  },
  {
    id: 'grid-path',
    label: 'Grid interconnection path',
    question: 'Where does the utility or interconnection request stand today?',
    neededBy: ['power-availability', 'interconnection', 'financial'],
    scenarioShaping: false,
  },
  {
    id: 'fuel-supply',
    label: 'Fuel supply',
    question: 'How will on-site generation receive gas or fuel?',
    neededBy: ['power-availability', 'supply-chain-risk', 'financial'],
    scenarioShaping: false,
  },
  {
    id: 'nuclear',
    label: 'Reactor design class',
    question: 'Which reactor design class is in scope?',
    neededBy: ['power-availability', 'supply-chain-risk'],
    scenarioShaping: false,
  },
  {
    id: 'commercial',
    label: 'Commercial record',
    question: 'Which revenue arrangement and carbon commitment already exist?',
    neededBy: ['financial'],
    scenarioShaping: false,
  },
];

/** `data-field-id` of the decision card in the Design view. */
export function stage2FocusField(id: Stage2DecisionId): string {
  return `stage2:${id}`;
}

/**
 * Site Project Intent schema keys that early screening records: the four
 * essentials plus the optional "Sharpen the screen" facts. Every other key
 * (cooling, technology, PUE / power factor, tier, redundancy, reactor class,
 * firm capacity, rights, offtake, carbon, attestations…) is a Stage 2
 * decision field — during screening it is listed as later work, never as a
 * screening gap.
 */
const SCREENING_INTENT_FIELD_KEYS: ReadonlySet<string> = new Set([
  'powerRequirement',
  'powerRequirementMW',
  'powerBasis',
  'workloadProfile',
  'inServiceDate',
  'waterSource.source',
  'fuelAccess.supply',
  'interconnectionStatus.queueStatus',
]);

export function isScreeningIntentFieldKey(key: string): boolean {
  return SCREENING_INTENT_FIELD_KEYS.has(key);
}

type FieldState = 'decided' | 'unknown' | 'open';

function answered(value: boolean, intent: ProjectIntent | null | undefined, unknownKey: string): FieldState {
  if (isIntentFieldExplicitlyUnknown(intent, unknownKey)) return 'unknown';
  return value ? 'decided' : 'open';
}

function combine(states: readonly FieldState[]): Stage2DecisionStatus {
  if (states.some(state => state === 'open')) return 'open';
  if (states.some(state => state === 'unknown')) return 'recorded-unknown';
  return 'decided';
}

function phaseHasTechnology(phase: NonNullable<ProjectIntent['phases']>[number]): boolean {
  return (phase.powerSourceConfig?.components.length ?? 0) > 0
    || (phase.powerSource != null && phase.powerSource !== 'undecided');
}

/** A nuclear source is in play through the stance or a declared phase source. */
export function nuclearInScope(intent: ProjectIntent | null | undefined): boolean {
  if (!intent) return false;
  if (intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering') return true;
  return (intent.phases ?? []).some(phase =>
    phase.powerSource === 'smr' || phase.powerSource === 'large-nuclear'
    || (phase.powerSourceConfig?.components ?? []).some(c => c === 'smr' || c === 'large-nuclear'));
}

export interface Stage2StatusInputs {
  intent: ProjectIntent | null | undefined;
  btmStrategy?: BtmStrategy;
  review?: Stage2DecisionReview | null;
}

function baseStatus(id: Stage2DecisionId, inputs: Stage2StatusInputs): Stage2DecisionStatus {
  // A site with no intent resolves exactly like an empty intent: nothing is
  // answered, and the not-applicable rules (strategy, nuclear scope) still
  // apply.
  const intent = inputs.intent;
  const strategy = inputs.btmStrategy;
  switch (id) {
    case 'power-supply': {
      if (!strategy) return 'open';
      if (strategy === 'grid-only') return 'decided';
      const phases = intent?.phases ?? [];
      // Unphased sites record the technology in the scenario itself.
      return phases.length === 0 || phases.every(phaseHasTechnology) ? 'decided' : 'open';
    }
    case 'cooling':
      return combine([
        answered(intent?.coolingStrategy != null && intent.coolingStrategy !== 'undecided', intent, 'coolingStrategy'),
        answered(intent?.bopHeatRejection != null && intent.bopHeatRejection !== 'unknown', intent, 'bopHeatRejection'),
      ]);
    case 'build-phases': {
      const phases = intent?.phases ?? [];
      const datesSet = phases.length > 0
        ? phases.every(phase => !!phase.targetInServiceDate?.trim()
          && (phase.capacityMW != null || (phase.capacityBucket != null && phase.capacityBucket !== 'undecided')))
        : !!intent?.inServiceDate?.trim();
      return combine([answered(datesSet, intent, 'timeline')]);
    }
    case 'design-basis': {
      const pue = intent?.pueValue;
      return combine([
        answered(typeof pue === 'number' && Number.isFinite(pue) && pue >= 1, intent, 'pueValue'),
        answered(intent?.datacenterTierTarget != null || intent?.redundancyClass != null, intent, 'datacenterTierTarget'),
      ]);
    }
    case 'firm-capacity':
      if (isFullyIslandedIntent(intent, strategy)) return 'not-applicable';
      return combine([answered(intent?.capacityCreditIntent?.target != null, intent, 'capacityCreditIntent')]);
    case 'water-supply':
      return combine([answered(hasDeclaredWaterSource(intent?.waterSource), intent, 'waterSource')]);
    case 'grid-path':
      if (isFullyIslandedIntent(intent, strategy)) return 'not-applicable';
      return combine([answered(intent?.interconnectionStatus?.queueStatus != null, intent, 'interconnectionStatus')]);
    case 'fuel-supply':
      if (strategy === 'grid-only') return 'not-applicable';
      return combine([answered(intent?.fuelAccess?.supply != null, intent, 'fuelAccess')]);
    case 'nuclear':
      if (!nuclearInScope(intent)) return 'not-applicable';
      return combine([answered(intent?.reactorDesignClass != null, intent, 'reactorDesignClass')]);
    case 'commercial':
      return combine([
        answered(intent?.offtake?.structure != null, intent, 'offtake'),
        answered(intent?.carbonCommitment != null, intent, 'carbonCommitment'),
      ]);
    default: {
      const exhaustive: never = id;
      void exhaustive;
      return 'open';
    }
  }
}

/** Status of one decision; an open decision the user deferred reads `deferred`. */
export function resolveStage2DecisionStatus(
  id: Stage2DecisionId,
  inputs: Stage2StatusInputs,
): Stage2DecisionStatus {
  const status = baseStatus(id, inputs);
  if (status === 'open' && inputs.review?.deferred?.[id]) return 'deferred';
  return status;
}
